import { Bookmark, Check, Pencil, Share2, Trash2 } from 'lucide-react';
import React, { useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

import { ConfirmDeleteDialog } from './ConfirmDeleteDialog';
import { SaveViewDialog } from './SaveViewDialog';
import { ShareViewDialog } from './ShareViewDialog';
import type { SavedView, SavedViewsPageConfig } from './types';
import { MAX_SAVED_VIEWS_PER_PAGE } from './types';
import { useSavedViews } from './useSavedViews';

interface SavedViewsMenuProps {
  config: SavedViewsPageConfig;
  organizationId: string;
}

export const SavedViewsMenu = ({ config, organizationId }: SavedViewsMenuProps) => {
  const {
    views,
    activeViewId,
    isFull,
    saveCurrentView,
    renameView,
    deleteView,
    applyView,
    shareUrlFor,
    pendingName,
    clearPendingName,
  } = useSavedViews({ config, organizationId });

  const handleSave = (name: string): boolean => {
    const ok = saveCurrentView(name);
    // Consume a shared-link name once saved so later saves don't re-prefill it.
    if (ok) clearPendingName();
    return ok;
  };

  const [menuOpen, setMenuOpen] = useState(false);
  const [saveOpen, setSaveOpen] = useState(false);
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState('');
  const [deletingView, setDeletingView] = useState<SavedView | null>(null);
  const [shareView, setShareView] = useState<SavedView | null>(null);

  const confirmDelete = () => {
    if (deletingView) deleteView(deletingView.id);
    setDeletingView(null);
  };

  const beginRename = (view: SavedView) => {
    setRenamingId(view.id);
    setRenameValue(view.name);
  };

  const commitRename = () => {
    if (renamingId) renameView(renamingId, renameValue);
    setRenamingId(null);
  };

  const handleApply = (view: SavedView) => {
    applyView(view);
    setMenuOpen(false);
  };

  const handleSaveClick = () => {
    if (isFull) {
      toast.error(
        `You can keep up to ${MAX_SAVED_VIEWS_PER_PAGE} views — delete one to save a new one.`,
      );
      return;
    }
    setMenuOpen(false);
    setSaveOpen(true);
  };

  return (
    <>
      <Popover open={menuOpen} onOpenChange={setMenuOpen}>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm">
            <Bookmark className="mr-1.5 h-4 w-4" />
            Saved views
          </Button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-80 p-2">
          {views.length === 0 ? (
            <p className="px-2 py-3 text-sm text-muted-foreground">
              No saved views yet. Save your current filters to reuse them later.
            </p>
          ) : (
            <ul className="max-h-72 space-y-0.5 overflow-y-auto">
              {views.map(view => (
                <li key={view.id} className="group flex items-center gap-1 rounded-md">
                  {renamingId === view.id ? (
                    <Input
                      autoFocus
                      value={renameValue}
                      onChange={e => setRenameValue(e.target.value)}
                      onBlur={commitRename}
                      onKeyDown={e => {
                        if (e.key === 'Enter') commitRename();
                        if (e.key === 'Escape') setRenamingId(null);
                      }}
                      // ring-inset keeps the focus ring inside the input so the
                      // popover's padding/rounded edge doesn't clip it unevenly.
                      className="mx-1 h-8 focus-visible:ring-inset"
                      aria-label="Rename view"
                    />
                  ) : (
                    <>
                      <button
                        type="button"
                        onClick={() => handleApply(view)}
                        className="flex-1 truncate rounded-md px-2 py-1.5 text-left text-sm hover:bg-accent"
                        title={view.name}
                      >
                        {view.name}
                      </button>
                      {/* Tick the active view; it yields to the row actions on
                          hover so the two never collide on the right edge. */}
                      <Check
                        aria-label="Active view"
                        className={cn(
                          'mr-1.5 h-4 w-4 shrink-0 text-primary group-hover:hidden',
                          view.id === activeViewId ? 'opacity-100' : 'opacity-0',
                        )}
                      />
                      <div className="hidden items-center gap-1 group-hover:flex">
                        <button
                          type="button"
                          onClick={() => setShareView(view)}
                          className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
                          aria-label={`Share ${view.name}`}
                          title="Share"
                        >
                          <Share2 className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => beginRename(view)}
                          className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground"
                          aria-label={`Rename ${view.name}`}
                          title="Rename"
                        >
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeletingView(view)}
                          className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-destructive"
                          aria-label={`Delete ${view.name}`}
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}

          {/* Hidden mid-rename — the only actions then are commit (Enter/blur) or
              cancel (Escape); an unrelated "save" here just invites a misclick. */}
          {renamingId === null && (
            <div className="mt-1 border-t pt-1">
              <button
                type="button"
                onClick={handleSaveClick}
                className="w-full rounded-md px-2 py-1.5 text-left text-sm font-medium text-primary hover:bg-accent"
              >
                Save current view…
              </button>
            </div>
          )}
        </PopoverContent>
      </Popover>

      <SaveViewDialog
        open={saveOpen}
        onOpenChange={setSaveOpen}
        defaultName={pendingName}
        onSave={handleSave}
      />

      {shareView && (
        <ShareViewDialog
          open={!!shareView}
          onOpenChange={open => !open && setShareView(null)}
          viewName={shareView.name}
          shareUrl={shareUrlFor(shareView)}
        />
      )}

      {deletingView && (
        <ConfirmDeleteDialog
          open={!!deletingView}
          onOpenChange={open => !open && setDeletingView(null)}
          viewName={deletingView.name}
          onConfirm={confirmDelete}
        />
      )}
    </>
  );
};
